import { useState, useEffect } from "react";
import { UI_CONFIG, RECRUITMENT_CONFIG } from "../constants/index";

export default function StorySequence({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isFastForwarded, setIsFastForwarded] = useState(false);

  // Load firstName from localStorage on component mount
  useEffect(() => {
    const savedFirstName = localStorage.getItem("userFirstName");
    if (savedFirstName) {
      setFirstName(savedFirstName);
    }
    setIsVisible(true);
  }, []);

  const handleSkipStory = () => {
    // Complete the story sequence and proceed to the next part (email form)
    onComplete();
  };

  // Typewriter effect for text animation with pauses
  useEffect(() => {
    if (currentStep === 1) return; // Skip for step 1 (name input)
    
    // If fast forwarded, show all text immediately
    if (isFastForwarded) {
      setDisplayedText(getStepText(currentStep));
      setIsTyping(false);
      return;
    }
    
    setIsTyping(true);
    const text = getStepText(currentStep);
    let index = 0;
    
    const typeInterval = setInterval(() => {
      if (index <= text.length) {
        // Get the text up to current index and format it
        const currentText = text.slice(0, index);
        setDisplayedText(currentText);
        index++;
      } else {
        setIsTyping(false);
        clearInterval(typeInterval);
      }
    }, 40); // Slightly faster typing

    return () => clearInterval(typeInterval);
  }, [currentStep, firstName, isFastForwarded]);

  const getStepText = (step) => {
    const storyTexts = {
      1: "Prêt(e) à sauver une entreprise ?",
      2: `Jeudi 16h. Premier appel.\n\nTu contactes **Bright Studio**, une agence en marketing :\n\n— "Bonjour, je m'appelle ${firstName}, **Chef de Projet** chez **Junior ISEP**. Nous sommes une association qui propose des prestations dans le numérique."\n\n— "**Vous tombez à pic !** Notre logiciel est une catastrophe !"\n\nTon cœur s'emballe. Tu proposes un rendez-vous **pour le lendemain**.\n\nLe client accepte ...`,
      3: `**Lendemain 10h05.** Le client a 5 minutes de retard.\n\nTu lances ta présentation, **stressé(e) mais déterminé(e)**.\n\nLe client est **convaincu** et te demande une **proposition commerciale** dans les **24h**.\n\n**48h plus tard**, tu lui envoies le contrat :\n Il signe.\n\n**Ton premier projet est OFFICIEL ${firstName} !**`,
      4: "Ce petit aperçu, c'est celui de **tous nos Chefs de Projet**.\n\nEn rejoignant Junior ISEP, tu choisis de :\n\n• **Aider des entreprises** à résoudre leurs problèmes\n\n• **Gagner en confiance** et monter en compétences\n\n• Avoir une **expérience** qui fait la différence sur le **CV**\n\nPrêt(e) à vivre cette aventure ?"
    };
    
    return storyTexts[step] || "";
  };

  const formatTextWithBold = (text) => {
    // Replace **text** with <strong>text</strong> for real-time formatting
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const formatTextWithBoldAndItalic = (text) => {
    // First handle bold text
    let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Then handle italic text
    processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    return processedText;
  };

  const getStepButtonText = (step) => {
    const buttonTexts = {
      1: "C'est parti",
      2: "Le rendez-vous",
      3: "Passer à la suite",
      4: "Je candidate"
    };
    
    return buttonTexts[step] || "Continuer";
  };

  const handleNameSubmit = () => {
    if (firstName.trim()) {
      localStorage.setItem("userFirstName", firstName.trim());
      setCurrentStep(2);
    }
  };

  const handleNextStep = () => {
    if (currentStep < 4) {
      // Start transition
      setIsTransitioning(true);
      
      // Wait for fade out, then change step
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsTransitioning(false);
        // Reset fast forward for new step
        setIsFastForwarded(false);
      }, 300); // Match transition duration
    } else {
      // Final step - open recruitment form in new tab
      window.open('https://juniorisep.quillforms.app/forms/recrutement-cdp-2025-2026/', '_blank');
      // Then complete the story to go to the main app
      onComplete();
    }
  };

  const handleFastForward = () => {
    setIsFastForwarded(true);
    setDisplayedText(getStepText(currentStep));
    setIsTyping(false);
  };

  const renderHeader = () => (
    <div className={`flex items-center pb-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className={`${UI_CONFIG.LOGO_SIZE.MEDIUM}`}>
        <img 
          src="/static/juniorisep.svg" 
          alt="Logo" 
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="flex-1 flex flex-col justify-center max-w-lg">
      <div className="mb-6">
        <h1 className="mb-3 leading-tight text-left">
          <span className="text-4xl md:text-6xl font-bold text-gray-900">99%</span>
          <span className="text-lg md:text-2xl font-bold text-gray-900"> des étudiants de l'Isep ne vivront jamais ça.</span>
        </h1>
        <p className="text-[12px] text-gray-600 leading-relaxed text-justify mb-4">
          Gérer un vrai projet client, signer un contrat, suivre des missions, et booster ton CV avant d'obtenir ton diplôme.
        </p>
        <p className="text-[12px] text-gray-600 leading-relaxed text-justify mb-6">
          Clique sur <strong>"C'est parti"</strong> pour plonger dans la vie d'un Chef de Projet.{" "}
          <strong>Une surprise t'attend à la fin.</strong>
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1 text-left text-[13px] font-bold">
            Quel est ton prénom, futur Chef de Projet ?
          </label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors text-base bg-white shadow-sm hover:shadow-md text-[11px]"
            placeholder="Ton prénom"
            onKeyPress={(e) => e.key === 'Enter' && handleNameSubmit()}
          />
        </div>
        
        <div className="space-y-3">
          <button
            onClick={handleNameSubmit}
            disabled={!firstName.trim()}
            className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold py-4 px-8 rounded-lg hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-[13px] font-bold"
          >
            C'est parti
          </button>
        </div>
      </div>
    </div>
  );

  const renderStoryStep = () => {
    // Special layout for Step 2 (Jeudi 16h page) - tighter spacing and higher position
    const isStep2 = currentStep === 2;
    
    return (
      <div className={`flex-1 flex flex-col justify-start max-w-lg transition-all duration-300 ${
        isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      } ${isStep2 ? '-mt-8' : 'pt-4'}`}>
        
        {/* Fast Forward Button */}
        {isTyping && !isFastForwarded && (
          <div className="absolute bottom-20 right-0">
            <button
              onClick={handleFastForward}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200 focus:outline-none"
              title="Passer l'animation"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Passer
            </button>
          </div>
        )}
        

        
        <div className={`${isStep2 ? 'mb-4' : 'mb-4'}`}>
          <div className={`text-[14px] text-gray-700 leading-relaxed text-justify space-y-3 ${
          isStep2 ? 'min-h-[3rem] space-y-2' : 'min-h-[6rem] space-y-3'
        }`}>
          {displayedText.split('\n').map((line, index) => {
            if (line.trim() === '') {
              return <div key={index} className={isStep2 ? 'h-0' : 'h-2'} />;
            }
            
            // Check if line contains bold text (wrapped in **) or italic text (wrapped in *text*)
            if (line.includes('**') || line.includes('*')) {
              // First handle bold text
              let processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              // Then handle italic text
              processedLine = processedLine.replace(/\*(.*?)\*/g, '<em>$1</em>');
              
              // Split by HTML tags and render
              const parts = processedLine.split(/(<strong>.*?<\/strong>|<em>.*?<\/em>)/g);
              return (
                <p key={index} className={isStep2 ? 'mb-0' : 'mb-2'}>
                  {parts.map((part, partIndex) => {
                    if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
                      return <strong key={partIndex} className="font-bold">{part.slice(8, -9)}</strong>;
                    }
                    if (part.startsWith('<em>') && part.endsWith('</em>')) {
                      return <em key={partIndex} className="italic">{part.slice(4, -5)}</em>;
                    }
                    return part;
                  })}
                </p>
              );
            }
            
            return <p key={index} className={isStep2 ? 'mb-0' : 'mb-2'}>{line}</p>;
          })}
          {isTyping && <span className="animate-pulse">|</span>}
        </div>
        </div>
        
        {(!isTyping || isFastForwarded) && (
          <div className={isStep2 ? 'mb-24' : 'mb-20'}>
            <button
              onClick={handleNextStep}
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold py-4 px-8 rounded-lg hover:from-orange-600 hover:to-yellow-600 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl text-[13px] font-bold animate-pulse"
            >
              {getStepButtonText(currentStep)}
            </button>
            {currentStep === 4 && (
              <>
                <button
                  onClick={handleSkipStory}
                  className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white font-bold py-3 px-4 rounded-lg hover:from-purple-600 hover:via-pink-600 hover:to-red-600 transition-all duration-300 text-[12px] border-2 border-transparent hover:border-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 animate-pulse mt-2"
                >
                  Surprise
                </button>
                <p className="text-[10px] text-gray-500 text-center mt-2 leading-relaxed">
                  Même après avoir cliqué sur "Je candidate", tu peux revenir sur cet onglet pour la surprise.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderProgress = () => (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
      <div className="flex space-x-2">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i + 1}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              i + 1 <= currentStep 
                ? 'bg-gradient-to-r from-orange-500 to-yellow-500' 
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="pl-10 pr-10 w-full min-h-screen bg-white flex flex-col relative overflow-hidden scrollbar-hide">
      {renderHeader()}
      
      <div className={`flex-1 flex flex-col justify-center transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        {currentStep === 1 ? renderStep1() : renderStoryStep()}
      </div>

      {renderProgress()}
    </div>
  );
}
